import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Spin, Button } from "antd";

import { useThunk } from "../../hooks/useThunk";
import { getRemixers, getTags } from "../../services/thunks/TrackThunks";
import {
  createPlaylist,
  getBasicInfoAllTracks,
  getPlaylistById,
  searchPlaylistTracks,
  updatePlaylist,
} from "../../services/thunks/playlistThunks";
import {
  getSubcategory,
  updateSubcategory,
} from "../../services/thunks/subcategoryThunks";
import { uploadToCloudflare } from "../../store/thunks/cloudFlareThunks";
import showToast from "../../utils/toastNotifications";
import {
  validatePlaylistTitle,
  validateTag,
} from "../../utils/validationUtils";
import { sanitizeHyphens } from "../../utils/formatting";

import Input from "../../components/Form/Input";
import Dropdown from "../../components/Form/Dropdown";
import DropdownWithInput from "../../components/Form/DropdownWithInput";
import FileUpload from "../../components/Form/FileUpload";
import MultiSelectedDropDownWithInput from "../../components/Form/MultiSelectedDropDownWithInput";
import CustomButton from "../../components/Buttons/CustomButton";
import ConfirmDeleteModal from "../../components/Modals/ConfirmDeleteModal";
import TrackSelectorModal from "../../components/Modals/TrackSelectorModal";
import {
  formatDateForBackend,
  localDateAsUtcMidnight,
} from "../../utils/dateFormating";

const AddNewPlaylist = () => {
  const { id } = useParams();
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [doGetRemixers] = useThunk(getRemixers);
  const [doGetSubcategory] = useThunk(getSubcategory);
  const [doGetTracksPaginated] = useThunk(getBasicInfoAllTracks);
  const [doSearchTracksPaginated] = useThunk(searchPlaylistTracks);
  const [doUpdatePlaylist, isUpdatingPlaylist] = useThunk(updatePlaylist);
  const [doCreatePlaylist, isCreatingPlaylist] = useThunk(createPlaylist);
  const [doGetTags] = useThunk(getTags);
  const [doUploadToCloudflare, isUploadingToCloudflare] =
    useThunk(uploadToCloudflare);
  const [doGetPlaylistById] = useThunk(getPlaylistById);

  const [playlistDetails, setPlaylistDetails] = useState({
    title: "",
    volume: "",
    subcategory: null,
    coverImage: null,
    tags: [],
    description: "",
    createdBy: null,
    publishedDate: localDateAsUtcMidnight(),
    tracks: [],
    socialLink: "",
  });

  const [subcategories, setSubcategories] = useState([]);
  const [remixers, setRemixers] = useState([]);
  const [assignedTracks, setAssignedTracks] = useState([]);
  const [showTrackSelector, setShowTrackSelector] = useState(false);
  const [tagsOptions, setTagsOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [trackToRemove, setTrackToRemove] = useState(null);
  const [tagErrors, setTagErrors] = useState({});

  const location = useLocation();

  useEffect(() => {
    if (location.state?.formData && !id) {
      setPlaylistDetails(location.state.formData);
      setAssignedTracks(location.state.assignedTracks || []);
    }
  }, []);

  useEffect(() => {
    const fetchDropdownData = async () => {
      const [subsRes, remixersRes, tagsRes] = await Promise.all([
        doGetSubcategory(),
        doGetRemixers({ token: auth.token }),
        doGetTags({ token: auth.token }),
      ]);

      const normalizedSubcategories =
        subsRes?.response?.data && Array.isArray(subsRes.response.data)
          ? subsRes.response.data
              .filter((s) => s.name.toLowerCase() !== "uncategorized")
              .map((s) => ({ _id: s._id, name: s.name }))
          : [];
      setSubcategories(normalizedSubcategories);

      const normalizedTags =
        tagsRes?.success === true && Array.isArray(tagsRes.response?.data.items)
          ? tagsRes.response?.data.items
              .filter((t) => t.tagName.toLowerCase() !== "unavailable")
              .map((t) => ({
                _id: t._id,
                name: t.tagName,
              }))
          : [];
      setTagsOptions(normalizedTags);

      const normalizedRemixers =
        remixersRes.success === true &&
        Array.isArray(remixersRes.response?.data.items)
          ? remixersRes.response?.data.items.map((r) => ({
              _id: r._id,
              name: r.remixerName,
            }))
          : [];
      setRemixers(normalizedRemixers);
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (id) {
      const fetchPlaylist = async () => {
        const res = await doGetPlaylistById({ id: id, token: auth.token });
        if (res?.success) {
          const playlistData = res.response.data;

          const normalizedTags = (playlistData.tags || []).map((tag) => ({
            _id: tag._id,
            name: tag.tagName,
            isCustom: false,
          }));
          //add parent and edit ids
          const tracksWithDetails = playlistData.tracks.map((t) => ({
            _id: t._id,
            parentTrackId: t.track,     
            editId: t.edit,              
            trackId: t.trackId,
            trackTitle: t.trackTitle,
            editTitle: t.editTitle || "",
            artists: t.artistNames || "Unknown Artist",
            artistNames: t.artistNames || "Unknown Artist",
            bpm: t.bpm || "N/A",
            key: t.key || "N/A",
            versionType: t.versionType || "",
            coverArt: t.coverArt,
          }));

          setPlaylistDetails({
            ...playlistData,
            publishedDate: playlistData.publishedDate?.split("T")[0],
            tags: normalizedTags,
          });

          setAssignedTracks(tracksWithDetails);
        } else {
          showToast("error", "Failed to fetch playlist");
        }
      };
      fetchPlaylist();
    }
  }, [id]);

  // Handle paginated track loading for modal
  const handleLoadTrackPage = async (pageSize, pageNum) => {
    try {
      const result = await doGetTracksPaginated({
        token: auth.token,
        page: pageNum,
        limit: pageSize,
      });

      if (result.success && result.response?.data) {
        const data = result.response.data;
        return {
          tracks: Array.isArray(data.tracks) ? data.tracks : [],
          total: data.total || 0,
          hasMore: data.hasMore ?? false,
        };
      }

      return { tracks: [], total: 0, hasMore: false };
    } catch (error) {
      console.error("Error loading tracks:", error);
      return { tracks: [], total: 0, hasMore: false };
    }
  };

  // Handle search in modal
  const handleSearchTracks = async (searchTerm, pageSize, pageNum) => {
    try {
      // Create a temporary thunk call for search since we don't have a dedicated thunk
      const result = await doSearchTracksPaginated({
        token: auth.token,
        search: searchTerm,
        page: pageNum,
        limit: pageSize,
      });


      if (!result.success) {
        throw new Error("Search failed");
      }

      const data = result.response.data || result.response;

      return {
        tracks: Array.isArray(data.tracks) ? data.tracks : [],
        total: data.total || 0,
        hasMore: data.hasMore ?? false,
      };
    } catch (error) {
      console.error("Error searching tracks:", error);
      return { tracks: [], total: 0, hasMore: false };
    }
  };


  const handleTrackSelect = (track) => {
    const isAlreadyAssigned = assignedTracks.some(
      (t) => t.editId === track.editId || t.editId === track._id
    );

    if (isAlreadyAssigned) {
      showToast("error", "This edit is already assigned to this playlist");
      return;
    }

    const updatedTrack = {
      _id: track._id,                           
      parentTrackId: track.parentTrackId,      
      editId: track.editId,                     
      trackId: track.trackId,
      trackTitle: track.trackTitle,
      editTitle: track.editTitle || "",
      artistNames: track.artists || "Unknown Artist",
      artists: track.artists || "Unknown Artist",
      bpm: track.bpm || "N/A",
      key: track.key || "N/A",
      versionType: track.versionType || "",
    };

    setAssignedTracks((prev) => [...prev, updatedTrack]);

    showToast(
      "success",
      `Track "${track.trackTitle}"${
        track.editTitle ? ` (${track.editTitle})` : ""
      } added successfully`
    );
  };

  const handleRemoveTrack = (idx) => {
    const updated = [...assignedTracks];
    const removedTrack = updated[idx];
    const trackId = updated[idx]._id;
    updated.splice(idx, 1);
    setAssignedTracks(updated);
    setPlaylistDetails((prev) => ({
      ...prev,
      tracks: prev.tracks.filter((id) => id !== trackId),
    }));
    showToast(
      "success",
      `Track "${removedTrack.trackTitle}" removed successfully`
    );
  };

  const validateField = (name, value) => {
    switch (name) {
      case "title":
        return validatePlaylistTitle(value);
      case "volume":
        if (value && !/^[1-9][0-9]{0,2}$/.test(value)) {
          return "Volume must be a positive integer (max 3 digits)";
        }
        break;
      case "description":
        if (value.length > 500) {
          return "Description max 500 characters";
        }
        break;
      case "coverImage":
        if (!value || (!value.url && !value.name)) {
          return "Cover image is required";
        }
        break;
      case "createdBy":
        if (value?.name?.length > 50) {
          return "Created By must be under 50 characters";
        }
        break;
      case "socialLink":
        if (playlistDetails.createdBy && !value?.trim()) {
          return "Social media link is required when creator is selected";
        }
        if (value && value.trim()) {
          try {
            new URL(value);
          } catch {
            return "Invalid URL format";
          }
        }
        break;
      case "publishedDate":
        if (!value) {
          return "Published date is required";
        }
        break;
      default:
        return "";
    }
    return "";
  };

  const uploadCoverImage = async (file) => {
    const res = await doUploadToCloudflare({ file, type: "images" });
    const data = res.response;

    if (!data || !data.key || !data.url) {
      throw new Error("Image upload failed");
    }

    return {
      key: data.key,
      url: data.url,
    };
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    const tagValidationErrors = playlistDetails.tags
      .map((tag) => validateTag(tag.name))
      .filter((msg) => msg);

    if (tagValidationErrors.length > 0) {
      setTagErrors(tagValidationErrors);
      showToast("error", "Please fix tag validation errors");
      setIsLoading(false);
      return;
    }

    if (hasFormErrors) {
      showToast("error", "Please fix form errors before submitting");
      setIsLoading(false);
      return;
    }

    let uploadedCoverImage = playlistDetails.coverImage;

    if (
      playlistDetails.coverImage &&
      !playlistDetails.coverImage.url &&
      !playlistDetails.coverImage.key
    ) {
      uploadedCoverImage = await uploadCoverImage(playlistDetails.coverImage);

      if (!uploadedCoverImage?.url || !uploadedCoverImage?.key) {
        showToast("error", "Image upload failed");
        setIsLoading(false);
        return;
      }

      showToast("success", "Image upload successful");
    }

    if (!uploadedCoverImage?.url || !uploadedCoverImage?.key) {
      showToast("error", "Missing cover image");
      setIsLoading(false);
      return;
    }

    // Build tracks with version IDs - backend will resolve to track/edit on retrieval
    const updatedPlaylistDetails = {
      ...playlistDetails,
      coverImage: uploadedCoverImage,
      tracks: assignedTracks.map((track) => ({
        track: track.parentTrackId,
        edit: track.editId,
        trackId: track.trackId,
      })),
    };

    try {
      const effectiveId = id || playlistDetails?._id;
      if (effectiveId) {
        const res = await doUpdatePlaylist({
          payload: updatedPlaylistDetails,
          id: effectiveId,
          isUpdate: true,
        });
        if (res.success) {
          showToast("success", "Playlist updated successfully");
          navigate("/manage-playlist");
        } else {
          showToast("error", res.error.message || "Error updating playlist");
        }
      } else {
        const res = await doCreatePlaylist({
          payload: updatedPlaylistDetails,
          isUpdate: false,
        });
        if (res.success) {
          showToast("success", "Playlist created successfully");
          navigate("/manage-playlist");
        } else {
          showToast("error", res.error.message || "Error creating playlist");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      showToast("error", "Failed to process playlist submission");
    } finally {
      setIsLoading(false);
    }
  };

  const hasFormErrors = Object.values(formErrors).some(
    (err) => err && err.trim() !== ""
  );

  return (
    <div className="min-h-screen p-10 text-white">
      <div className="bg-[#0D0D0D] rounded-lg border border-[#505050] p-6 mb-6">
        <div className="text-[20px] font-[700] font-Public_Sans text-white mb-6">
          Playlist Details
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Input
            label="Title"
            placeholder="Enter Title"
            value={playlistDetails.title}
            onChange={(e) => {
              const value = e.target.value;
              setPlaylistDetails((prev) => ({ ...prev, title: value }));
              setFormErrors((prev) => ({ ...prev, title: null }));
            }}
            onBlur={() =>
              setFormErrors((prev) => ({
                ...prev,
                title: validateField("title", playlistDetails.title),
              }))
            }
            isRequired={true}
            errorMessage={formErrors.title}
            inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
            outerContainerStyle="mt-[-1px]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
          />
          <Dropdown
            label="Subcategory / Label"
            placeholder="Select Subcategory / Label"
            options={subcategories}
            displayKey="name"
            idKey="_id"
            isRequired={true}
            defaultOption={playlistDetails.subcategory}
            onSelect={(v) => {
              setPlaylistDetails((prev) => ({ ...prev, subcategory: v }));
              setFormErrors((prev) => ({ ...prev, subcategory: null }));
            }}
            onBlur={() =>
              setFormErrors((prev) => ({
                ...prev,
                subcategory: validateField(
                  "subcategory",
                  playlistDetails.subcategory
                ),
              }))
            }
            errorMessage={formErrors.subcategory}
            wrapperClassName="text-[#E2E2E2] text-[15px] font-[300] font-['Public Sans'] w-full min-w-[281px]"
            dropdownClassName="bg-[#2D2D2D] border-[#808080] border z-20"
            optionClassName="text-[#E2E2E2] py-2"
            buttonClassName="h-[45px]"
            filterClassName="border-t border-[#505050]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderClassName="text-[#808080] text-[14px] font-['Public Sans'] font-normal"
            isSearchable={true}
          />
          <Input
            type="number"
            label="Volume (Optional)"
            placeholder="Vol. X"
            value={playlistDetails.volume}
            onChange={(e) =>
              setPlaylistDetails({ ...playlistDetails, volume: e.target.value })
            }
            onBlur={() =>
              setFormErrors((prev) => ({
                ...prev,
                volume: validateField("volume", playlistDetails.volume),
              }))
            }
            errorMessage={formErrors.volume}
            inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
            outerContainerStyle="mt-[-1px]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
          />
          <FileUpload
            label="Cover Image"
            type="image"
            defaultValue={playlistDetails.coverImage}
            maxSize={5}
            isRequired={true}
            onFileUpload={(f) =>
              setPlaylistDetails({ ...playlistDetails, coverImage: f })
            }
            onBlur={() =>
              setFormErrors((prev) => ({
                ...prev,
                coverImage: validateField(
                  "coverImage",
                  playlistDetails.coverImage
                ),
              }))
            }
            showErrorBelow={false}
            error={formErrors.coverImage}
            containerStyle="w-full min-w-[220px] w-full h-full "
            labelStyle="text-sm mb-[8px] font-medium font-['Public_Sans'] text-[#E3E3E3] whitespace-nowrap"
          />
          <div className="col-span-2">
            <MultiSelectedDropDownWithInput
              label="Tags"
              options={tagsOptions}
              defaultSelected={playlistDetails.tags}
              onSelect={(newTags) => {
                const cleanTags = newTags.map((t) => ({
                  ...t,
                  name: sanitizeHyphens(t.name),
                }));

                setPlaylistDetails((prev) => ({ ...prev, tags: cleanTags }));

                const errors = cleanTags
                  .map((tag) => validateTag(tag.name))
                  .filter(Boolean);
                setTagErrors(errors);
              }}
              error={tagErrors.length > 0 ? tagErrors[0] : ""}
            />
          </div>
          <div className="col-span-2">
            <Input
              label="Description"
              type="textarea"
              value={playlistDetails.description}
              onChange={(e) =>
                setPlaylistDetails({
                  ...playlistDetails,
                  description: e.target.value,
                })
              }
              onBlur={() =>
                setFormErrors((prev) => ({
                  ...prev,
                  description: validateField(
                    "description",
                    playlistDetails.description
                  ),
                }))
              }
              errorMessage={formErrors.description}
              inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[120px]"
              outerContainerStyle="mt-[-1px]"
              labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
              placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
            />
          </div>
          <DropdownWithInput
            label="Created By"
            placeholder="Created By"
            options={remixers}
            displayKey="name"
            idKey="_id"
            defaultOption={playlistDetails.createdBy}
            onSelect={(v) => {
              setPlaylistDetails({
                ...playlistDetails,
                createdBy: v,
                socialLink: v ? playlistDetails.socialLink : "",
              });

              setFormErrors((prev) => ({
                ...prev,
                createdBy: null,
                socialLink: v ? prev.socialLink : null,
              }));
            }}
            error={formErrors.createdBy}
            wrapperClassName="text-[#E2E2E2] text-[15px] font-[300] font-['Public Sans'] w-full min-w-[281px]"
            dropdownClassName="bg-[#2D2D2D] border-[#808080] border z-20"
            optionClassName="text-[#E2E2E2] py-2"
            buttonClassName="h-[45px]"
            filterClassName="border-t border-[#505050]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderClassName="text-[#808080] text-[14px] font-['Public Sans'] font-normal"
            isSearchable={true}
            allowClear={true}
          />
          <Input
            label="Social Media Link"
            placeholder="Enter social media link"
            value={playlistDetails.socialLink}
            onChange={(e) =>
              setPlaylistDetails({
                ...playlistDetails,
                socialLink: e.target.value,
              })
            }
            onBlur={() =>
              setFormErrors((prev) => ({
                ...prev,
                socialLink: validateField(
                  "socialLink",
                  playlistDetails.socialLink,
                  playlistDetails.createdBy
                ),
              }))
            }
            isRequired={playlistDetails.createdBy}
            disabled={!playlistDetails.createdBy}
            errorMessage={formErrors.socialLink}
            inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
            outerContainerStyle="mt-[-1px]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
          />

          <Input
            label="Published Date"
            type="date"
            value={playlistDetails.publishedDate}
            isRequired={true}
            onChange={(e) =>
              setPlaylistDetails({
                ...playlistDetails,
                publishedDate: e.target.value,
              })
            }
            isLocalUTC={true}
            errorMessage={formErrors.publishedDate}
            inputStyle="bg-[#45444433] border-[1px] border-[#808080] w-full h-[45px]"
            outerContainerStyle="mt-[-1px]"
            labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
            placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
          />
        </div>
      </div>

      {/* Assigned Track Table */}
      <div className="rounded-lg mb-6">
        <div className="bg-[#111111D6] border-[#505050] rounded-lg p-[20px] border">
          <div className="text-[20px] font-[700] font-Public_Sans mb-4">
            Add Tracks to Playlist
          </div>
          <div className="flex gap-4 mb-4 justify-center items-center">
            <CustomButton
              className=" h-[40px] text-[18px] font-normal font-inter"
              onClick={() => {
                setShowTrackSelector(true);
              }}
            >
              + Assign Existing Track
            </CustomButton>
            <CustomButton
              className=" h-[40px] text-[18px] font-normal font-inter"
              onClick={() =>
                navigate("/add-tracks", {
                  state: {
                    fromAddPlaylist: true,
                    formData: playlistDetails,
                    assignedTracks: assignedTracks,
                    playlistId: id || playlistDetails?._id,
                  },
                })
              }
            >
              + Upload New Track
            </CustomButton>
          </div>
        </div>
        <div className="rounded-xl overflow-hidden border border-[#444] mt-6">
          <table className="w-full text-[14px] text-[#FFFFFF]">
            <thead className="text-[#888] bg-[#0D141B] border-b border-[#444]">
              <tr>
                <th className="py-2 px-4 text-left ">ID</th>
                <th className="py-2 px-4 text-left w-[30%]">Title</th>
                <th className="py-2 px-4 text-left w-[20%]">Artist(s)</th>
                <th className="py-2 px-4 text-left">Key(s)</th>
                <th className="py-2 px-4 text-left">BPM(s)</th>
                <th className="py-2 px-4 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {assignedTracks.map((track, idx) => (
                <tr key={idx} className="border-b border-[#444]">
                  <td className="py-2 px-4">{track.trackId}</td>
                  <td className="py-2 px-4">
                    <span
                      className="cursor-default"
                      {...(() => {
                        const fullTitle = track.editTitle
                          ? `${track.trackTitle} (${track.editTitle})`
                          : track.trackTitle || "";
                        return fullTitle.length > 20
                          ? { title: fullTitle }
                          : {};
                      })()}
                    >
                      {(() => {
                        const fullTitle = track.editTitle
                          ? `${track.trackTitle} (${track.editTitle})`
                          : track.trackTitle || "";
                        return fullTitle.length > 40
                          ? `${fullTitle.slice(0, 40)}…`
                          : fullTitle;
                      })()}
                    </span>
                  </td>
                  <td className="py-2 px-4">
                    <span
                      className="cursor-default"
                      {...(track.artistNames?.length > 20
                        ? { title: track.artistNames }
                        : {})}
                    >
                      {track.artistNames ? track.artistNames.slice(0, 30) : ""}{" "}
                      {track.artistNames && track.artistNames.length > 30
                        ? "…"
                        : ""}
                    </span>
                  </td>
                  <td className="py-2 px-4">
                    <span title={track.key || ""} className="cursor-help">
                      {track.key}
                    </span>
                  </td>
                  <td className="py-2 px-4">
                    <span title={track.bpm || ""} className="cursor-help">
                      {track.bpm}
                    </span>
                  </td>
                  <td className="py-2 px-4">
                    <button
                      className="flex items-center justify-center gap-2 px-4 py-1 border border-[#C00707] text-[#C00707] rounded-[15px] text-sm w-[90px] hover:bg-[#330000] transition"
                      onClick={() => {
                        setTrackToRemove({
                          track: assignedTracks[idx],
                          index: idx,
                        });
                        setShowConfirm(true);
                      }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Track Modal */}
      {showTrackSelector && (
        <TrackSelectorModal
          visible={showTrackSelector}
          onClose={() => setShowTrackSelector(false)}
          onSelect={handleTrackSelect}
          onLoadPage={handleLoadTrackPage}
          onSearch={handleSearchTracks}
          title="Assign Track to Playlist"
          excludeIds={assignedTracks.map((track) => track.editId)}
          mode="playlist"
        />
      )}

      {showConfirm && (
        <ConfirmDeleteModal
          visible={showConfirm}
          onConfirm={async () => {
            if (trackToRemove) {
              handleRemoveTrack(trackToRemove.index);
              setShowConfirm(false);
              setTrackToRemove(null);
            }
          }}
          onCancel={() => {
            setShowConfirm(false);
            setTrackToRemove(null);
          }}
          title="Confirm Removal"
          content={`Are you sure you want to remove the track "${trackToRemove?.track?.trackTitle}" from the playlist?`}
        />
      )}

      <div className="flex justify-center gap-4 mt-6">
        <CustomButton
          textColor="text-[#818181] h-[45px] w-[145px]"
          className="text-[18px] font-jost"
          borderColor="border-[#818181] border-[2px]"
          onClick={() => navigate("/manage-playlist")}
        >
          Cancel
        </CustomButton>
        <CustomButton
          useGradientBorder
          className="h-[45px] w-[145px] text-[18px] font-jost"
          textColor="gradient"
          gradient="bg-gradient-to-b from-[#52FAF0] to-[#197AD0]"
          onClick={handleSubmit}
          disabled={
            isLoading ||
            hasFormErrors ||
            isUpdatingPlaylist ||
            isCreatingPlaylist ||
            isUploadingToCloudflare ||
            tagErrors.length > 0
          }
          loading={
            isLoading ||
            isUpdatingPlaylist ||
            isCreatingPlaylist ||
            isUploadingToCloudflare
          }
        >
          <span>{isLoading ? "Saving..." : "Save Playlist"}</span>
        </CustomButton>
      </div>
    </div>
  );
};

export default AddNewPlaylist;